/**
 * O suavizador de revelação do texto da bolha (11/09/2026).
 *
 * O SSE entrega o texto em pedaços irregulares — um parágrafo inteiro num
 * chunk, três palavras no seguinte, uma pausa de segundos enquanto uma tool
 * roda — e a bolha desenhava cada pedaço no instante em que chegava: o texto
 * entrava "aos pontos", em blocos, e lia como travamento (frames
 * `~/aawz-tmp/screencasts/23-07/`). Aqui a bolha passa a manter DOIS textos —
 * o que já CHEGOU e o que está NA TELA — e um laço de `requestAnimationFrame`
 * aproxima o segundo do primeiro a uma taxa constante, como se estivesse
 * digitando. É o efeito do Claude.ai/ChatGPT.
 *
 * Três regras que valem saber:
 *
 * - **A taxa é por TEMPO, não por frame** (`chars/ms`): 60 Hz, 120 Hz e um
 *   frame perdido dão a mesma velocidade de leitura. Base de ~250 chars/s; se
 *   o atraso passa de `LIMIAR_DE_ATRASO_CHARS`, acelera para não ficar para
 *   trás (drena o excedente em ~400ms); no `end` (`fechar()`), o que falta
 *   entra em no máximo `MS_DO_FECHO`.
 * - **O corte é em fronteira segura** (`corteSeguro`): fim de palavra, nunca
 *   no meio de uma — a menos que a "palavra" seja uma URL/id sem espaço há
 *   mais de `PALAVRA_LONGA_CHARS` — e nunca deixando um link `[texto](url`
 *   aberto (o markdown-it renderizaria o colchete literal e o trocaria por um
 *   link no frame seguinte). Ênfase aberta (`**`, `` ` ``, `~~`) não segura
 *   o corte: quem cuida dela é `fecharMarkdownAberto`, que o renderer aplica
 *   ao texto EXIBIDO — o negrito já nasce em negrito, sem piscar.
 * - **Sem `setInterval` pendurado**: um único frame agendado por vez, que
 *   não se reagenda quando a tela alcançou o recebido; `parar()` cancela o
 *   pendente (a bolha chama no `beforeDestroy`). `prefers-reduced-motion`
 *   desliga a suavização — a bolha nem cria o laço (`prefereMenosMovimento`).
 *
 * O relógio e o agendador são INJETÁVEIS (`agora`, `agendar`, `cancelar`):
 * o Jest 24 desta casa não dubla `requestAnimationFrame`, e o laço tem de ser
 * medido frame a frame.
 *
 * Sem `?.`/`??` (o Babel deste projeto não transpila).
 */

/**
 * ~140 caracteres por segundo: com o corte a cada `MS_ENTRE_PEDACOS`, dá um
 * pedaço de ~4 palavras a cada 180ms. Afinado com o owner na tela em
 * 21/09/2026 (250 → 200 → 140: "mais lento e com espaço entre os chunks").
 */
export const TAXA_BASE_CHARS_POR_MS = 0.14
/** Acima deste atraso a taxa sobe para a tela não ficar para trás. */
export const LIMIAR_DE_ATRASO_CHARS = 300
/** O excedente acima do limiar é drenado neste tempo. */
export const MS_PARA_ALCANCAR = 400
/** No `end`, tudo o que falta entra em no máximo isto — até o teto abaixo. */
export const MS_DO_FECHO = 300
/**
 * ★ [owner, 21/09/2026] Os TETOS de velocidade. Sem eles, o texto que chega
 * PRONTO (o atalho determinístico, o fecho depois da encenação do plano)
 * entrava em 400ms — *"renderizando TUDO de uma vez, muito esquisito e
 * bruto"*. Alcançar continua acontecendo, mas nunca mais rápido do que o olho
 * acompanha: ~300 chars/s no meio do turno e ~1.000 chars/s no fecho (um
 * `/panorama` de 1.500 chars leva 1,5s, não 0,3s). O fecho de um turno
 * STREAMADO não muda: o resto que sobra depois do `end` é pequeno (o alcance
 * o manteve perto), e cabe nos mesmos 300ms.
 */
export const TAXA_MAXIMA_DE_ALCANCE_CHARS_POR_MS = 0.22
export const TAXA_MAXIMA_DO_FECHO_CHARS_POR_MS = 1
/**
 * ★ [owner, 21/09/2026] O teto do texto que chega num BLOCO SÓ — o caminho do
 * Supervisor (`apiChat.js::_onDeltaDeTextoSombra`): nenhum `token`, o texto
 * inteiro num `text-delta` e o `end` logo atrás. A bolha persistida nasce
 * com tudo e revela do zero; sem teto próprio ela usaria o do fecho (1.000
 * chars/s), que existe para o RESTO pequeno de um turno streamado. Aqui o
 * "resto" é a resposta inteira, e o ritmo é o do alcance (~220 chars/s,
 * afinado com o owner na tela: 600 → 350 → 220, "mais lento e com espaço
 * entre os chunks", 21/09/2026). É um TETO de verdade: vale também sobre o
 * alcance, que sozinho subiria ao próprio teto enquanto o atraso é grande.
 * Os três números aqui são o botão de ritmo.
 */
export const TAXA_MAXIMA_DO_BLOCO_CHARS_POR_MS = 0.22
/**
 * ★ [owner, 21/09/2026] O texto entra em PEDAÇOS, não em fio contínuo. Com um
 * corte por frame (16ms) e o fade por palavra, trinta palavras estão acendendo
 * ao mesmo tempo e a frase inteira parece revelar de uma vez, da esquerda para
 * a direita. No Claude.ai o texto chega em chunks de poucas palavras, cada um
 * acendendo sozinho: aqui o orçamento continua correndo a cada frame (a taxa
 * não muda), mas o CORTE só acontece a cada `MS_ENTRE_PEDACOS` — a taxa base
 * dá ~3 palavras por pedaço, e o fade (`acender-palavras.js`) acende cada
 * pedaço como um bloco. O fecho de um resto pequeno respeita a cadência
 * também: são poucos pedaços, e cabem no prazo.
 */
export const MS_ENTRE_PEDACOS = 180
/** Um frame perdido (aba oculta, GC) não vira um salto de texto. */
export const DT_MAXIMO_MS = 100
/** Sem espaço há tantos caracteres, a "palavra" é URL/id e pode ser cortada. */
export const PALAVRA_LONGA_CHARS = 24
/** Um `[` sem `](...)` a esta distância é colchete literal, não link. */
export const LINK_MAXIMO_CHARS = 200
/**
 * Teto do orçamento acumulado enquanto o corte espera (palavra/link
 * inacabado): quando a espera termina, o que entra de uma vez é no máximo
 * uma linha, não a espera inteira.
 */
export const ACUMULADO_MAXIMO_CHARS = 96

const ESPACO = /\s/

/**
 * Caracteres por milissegundo para o atraso atual.
 *
 * ⚠️ No fecho a taxa é FIXADA no instante do `end` (`fechar()`), não
 * recalculada a cada frame: `atraso / MS_DO_FECHO` reavaliado sobre um atraso
 * que encolhe decai exponencialmente e nunca chega ao fim — medido no teste
 * do laço (312 de 479 caracteres depois dos 300ms).
 *
 * @param {number} atraso  `recebido.length - exibido.length`.
 * @param {{fecho?: boolean, teto?: number}=} opcoes  `fecho` — a taxa que drena
 *   `atraso` em `MS_DO_FECHO`; `teto` — o máximo do fecho (padrão
 *   `TAXA_MAXIMA_DO_FECHO_CHARS_POR_MS`; o bloco passa o dele).
 */
export function taxaDeRevelacao(atraso, opcoes) {
  const fecho = Boolean(opcoes && opcoes.fecho)
  const teto = (opcoes && opcoes.teto) || TAXA_MAXIMA_DO_FECHO_CHARS_POR_MS
  let taxa = TAXA_BASE_CHARS_POR_MS
  if (atraso > LIMIAR_DE_ATRASO_CHARS) {
    taxa = Math.max(taxa, Math.min(atraso / MS_PARA_ALCANCAR, TAXA_MAXIMA_DE_ALCANCE_CHARS_POR_MS))
  }
  if (fecho) taxa = Math.max(taxa, Math.min(atraso / MS_DO_FECHO, teto))
  // O teto EXPLÍCITO (o bloco) limita tudo — inclusive o alcance acima.
  if (opcoes && opcoes.teto) taxa = Math.min(taxa, opcoes.teto)
  return taxa
}

/**
 * O fim da última palavra completa dentro de `[de, alvo)` — a posição do
 * espaço que a fecha, de modo que o exibido termine na LETRA e não no espaço
 * —, ou `de` se a janela não tem espaço nenhum — salvo quando ela já é longa
 * o bastante para ser URL/id, aí corta em `alvo` mesmo.
 *
 * ⚠️ Terminar na letra importa para o markdown tolerante: `**renda **` não
 * fecha o negrito (o markdown-it exige o `**` colado à palavra) — medido ao
 * vivo em 11/09/2026, com o asterisco literal na tela.
 */
function fronteiraDePalavra(texto, de, alvo) {
  for (let i = alvo - 1; i >= de; i -= 1) {
    if (ESPACO.test(texto.charAt(i))) return i
  }
  if (alvo - de >= PALAVRA_LONGA_CHARS) return alvo
  return de
}

/**
 * Não deixa um `[texto](url` aberto no corte: se o link fecha mais adiante no
 * recebido, o corte pula para depois do `)`; se ainda não fechou, recua para
 * antes do `[`. Um `[` já EXIBIDO (antes de `de`) não tem volta, e um `[`
 * que não fecha em `LINK_MAXIMO_CHARS` é colchete literal.
 */
function recuarAntesDeLinkAberto(texto, de, corte) {
  const abre = texto.lastIndexOf('[', corte - 1)
  if (abre < de) return corte
  const fechaColchete = texto.indexOf(']', abre)
  const ehLink = fechaColchete !== -1 && texto.charAt(fechaColchete + 1) === '('
  if (fechaColchete !== -1 && !ehLink) return corte
  if (ehLink) {
    const fechaParentese = texto.indexOf(')', fechaColchete)
    if (fechaParentese !== -1) {
      return fechaParentese < corte ? corte : fechaParentese + 1
    }
  }
  if (texto.length - abre > LINK_MAXIMO_CHARS) return corte
  return abre
}

/**
 * Onde cortar o texto para exibir, dado o que já está na tela (`de`) e até
 * onde o orçamento deste frame chega (`alvo`).
 *
 * Devolve uma posição em `[de, texto.length]` — nunca recua o que já foi
 * exibido. No fecho a guarda de link é dispensada: o texto está completo, e o
 * que sobrou entra inteiro.
 *
 * @param {string} texto  O texto recebido.
 * @param {number} de     Quantos caracteres já estão na tela.
 * @param {number} alvo   Até onde o orçamento permite ir.
 * @param {{fecho?: boolean}=} opcoes
 */
export function corteSeguro(texto, de, alvo, opcoes) {
  const fecho = Boolean(opcoes && opcoes.fecho)
  const fim = texto.length
  if (alvo >= fim) return fecho ? fim : recuarAntesDeLinkAberto(texto, de, fim)
  const corte = fronteiraDePalavra(texto, de, alvo)
  if (fecho) return corte
  return Math.max(de, recuarAntesDeLinkAberto(texto, de, corte))
}

function contar(texto, marca) {
  const ocorrencias = texto.match(marca)
  return ocorrencias ? ocorrencias.length : 0
}

/**
 * O texto EXIBIDO com a ênfase inline que ficou aberta fechada no fim —
 * `**negrito`, `` `código`` e `~~riscado`` —, para o markdown-it renderizar o
 * trecho já formatado em vez de mostrar os asteriscos literais até o par
 * chegar. Dentro de um bloco de código cercado (```) nada é ênfase, e um
 * cerco aberto fica como está: o markdown-it já desenha o resto como código.
 *
 * `*`/`_` simples ficam de fora de propósito: ambíguos com item de lista e
 * com multiplicação, e o modelo escreve ênfase com `**`.
 */
export function fecharMarkdownAberto(texto) {
  const original = String(texto || '')
  if (contar(original, /```/g) % 2 === 1) return original
  let fechos = ''
  let semCodigo = original.replace(/```[\s\S]*?```/g, '').replace(/`[^`\n]*`/g, '')
  const ultimoAcento = semCodigo.lastIndexOf('`')
  if (ultimoAcento !== -1) {
    fechos += '`'
    semCodigo = semCodigo.slice(0, ultimoAcento)
  }
  if (contar(semCodigo, /\*\*/g) % 2 === 1) fechos += '**'
  if (contar(semCodigo, /~~/g) % 2 === 1) fechos += '~~'
  if (!fechos) return original
  // O fecho tem de ficar COLADO à última letra: `**renda **` é asterisco
  // literal para o markdown-it (o delimitador de fecho não pode vir depois de
  // espaço). O espaço final é invisível na tela; some sem custo.
  return original.replace(/\s+$/, '') + fechos
}

/** O SO pediu menos movimento: o texto entra como chega, sem suavização. */
export function prefereMenosMovimento() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false
  }
  const consulta = window.matchMedia('(prefers-reduced-motion: reduce)')
  return Boolean(consulta && consulta.matches)
}

function agendarFramePadrao(callback) {
  if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
    return window.requestAnimationFrame(callback)
  }
  return setTimeout(function () {
    callback(Date.now())
  }, 16)
}

function cancelarFramePadrao(handle) {
  if (typeof window !== 'undefined' && typeof window.cancelAnimationFrame === 'function') {
    window.cancelAnimationFrame(handle)
    return
  }
  clearTimeout(handle)
}

function agoraPadrao() {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now()
  }
  return Date.now()
}

/**
 * O laço que aproxima o texto exibido do recebido.
 *
 * Estado de INSTÂNCIA (uma por bolha), nunca de módulo — duas bolhas em
 * revelação (o fecho da anterior e o streaming da próxima) não podem dividir
 * um relógio.
 */
export class RevelacaoDeTexto {
  /**
   * @param {Object}   opcoes
   * @param {Function} opcoes.aoMudar      `(exibidoLen) => void` — a cada avanço.
   * @param {Function=} opcoes.aoConcluir  Só no fecho: quando a tela alcança o fim.
   * @param {number=}  opcoes.exibidoLen   Por onde começar (o prefixo já na tela).
   * @param {Function=} opcoes.agendar     `requestAnimationFrame` injetável (teste).
   * @param {Function=} opcoes.cancelar    `cancelAnimationFrame` injetável.
   * @param {Function=} opcoes.agora       `performance.now` injetável.
   */
  constructor(opcoes) {
    const config = opcoes || {}
    this._aoMudar = typeof config.aoMudar === 'function' ? config.aoMudar : function () {}
    this._aoConcluir = typeof config.aoConcluir === 'function' ? config.aoConcluir : function () {}
    this._agendar = config.agendar || agendarFramePadrao
    this._cancelar = config.cancelar || cancelarFramePadrao
    this._agora = config.agora || agoraPadrao
    this._texto = ''
    this._exibidoLen = Math.max(0, config.exibidoLen || 0)
    // Relógios do laço: o do último frame e o do último CORTE (a cadência dos
    // pedaços); `_handle` é o frame pendente.
    Object.assign(this, { _acumulado: 0, _ultimoTs: null, _ultimoCorteTs: null, _handle: null })
    this._fecho = false
    // A taxa do fecho, fixada em `fechar()` — ver `taxaDeRevelacao` —, e o
    // teto explícito do bloco, que vale por cima do alcance frame a frame.
    Object.assign(this, { _taxaDoFecho: 0, _tetoDoFecho: null })
    this._parada = false
  }

  get exibidoLen() {
    return this._exibidoLen
  }

  get texto() {
    return this._texto
  }

  /** O texto recebido até agora — o alvo que a tela persegue. */
  receber(texto) {
    this._texto = String(texto || '')
    if (this._exibidoLen > this._texto.length) {
      this._exibidoLen = this._texto.length
      this._aoMudar(this._exibidoLen)
    }
    this._agendarFrame()
  }

  /**
   * O `end` chegou: o que falta entra em no máximo `MS_DO_FECHO` — até o
   * teto, que o bloco do Supervisor troca pelo dele (`opcoes.teto`).
   */
  fechar(opcoes) {
    this._fecho = true
    this._tetoDoFecho = (opcoes && opcoes.teto) || null
    const atraso = this._texto.length - this._exibidoLen
    this._taxaDoFecho = taxaDeRevelacao(atraso, { fecho: true, teto: this._tetoDoFecho })
    this._agendarFrame()
  }

  /** Cancela o frame pendente; nenhum callback dispara depois disto. */
  parar() {
    this._parada = true
    if (this._handle === null) return
    this._cancelar(this._handle)
    this._handle = null
  }

  _alcancou() {
    return this._exibidoLen >= this._texto.length
  }

  _agendarFrame() {
    if (this._parada || this._handle !== null) return
    if (this._alcancou()) {
      if (this._fecho) this._aoConcluir()
      return
    }
    // Voltando da espera, o relógio recomeça: o tempo parado não é orçamento.
    if (this._ultimoTs === null) this._ultimoTs = this._agora()
    const self = this
    this._handle = this._agendar(function (ts) {
      self._frame(typeof ts === 'number' ? ts : self._agora())
    })
  }

  _frame(ts) {
    this._handle = null
    if (this._parada) return
    const dt = Math.min(Math.max(ts - this._ultimoTs, 0), DT_MAXIMO_MS)
    this._ultimoTs = ts
    this._avancar(dt, ts)
    if (this._alcancou()) {
      this._ultimoTs = null
      if (this._fecho) this._aoConcluir()
      return
    }
    this._agendarFrame()
  }

  _avancar(dt, ts) {
    const atraso = this._texto.length - this._exibidoLen
    let taxa = Math.max(taxaDeRevelacao(atraso), this._taxaDoFecho)
    if (this._tetoDoFecho) taxa = Math.min(taxa, this._tetoDoFecho)
    this._acumulado += dt * taxa
    // O teto só vale fora do fecho: ali o prazo manda, não a linha.
    if (!this._fecho) this._acumulado = Math.min(this._acumulado, ACUMULADO_MAXIMO_CHARS)
    // A cadência dos pedaços: o orçamento acumula a cada frame, o corte não.
    if (this._ultimoCorteTs !== null && ts - this._ultimoCorteTs < MS_ENTRE_PEDACOS) return
    const alvo = Math.floor(this._exibidoLen + this._acumulado)
    const corte = corteSeguro(this._texto, this._exibidoLen, alvo, { fecho: this._fecho })
    if (corte <= this._exibidoLen) return
    this._ultimoCorteTs = ts
    this._acumulado -= corte - this._exibidoLen
    this._exibidoLen = corte
    this._aoMudar(corte)
  }
}
